import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MapPin, Target as TargetIcon, PackageOpen, ArrowLeft, Plus, ArrowRight, Trash2, Layers, TrendingUp, RotateCcw, CalendarClock } from "lucide-react";

const PageHeader = ({ title, subtitle, icon: Icon }) => (
  <div className="flex items-start gap-3">
    {Icon && <div className="h-10 w-10 rounded-full bg-[#0F5E39] text-white flex items-center justify-center"><Icon size={18}/></div>}
    <div>
      <div className="font-serif text-2xl">{title}</div>
      {subtitle && <div className="text-xs text-[#4A5A50]">{subtitle}</div>}
    </div>
  </div>
);

/**
 * Combined Field-Sales extras page: Territories / Targets / Consignments / Daily Report.
 * Tabs kept simple; each section owns its own fetch and mutation.
 */
export default function FieldExtrasPage() {
  const [tab, setTab] = useState("territories");
  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <PageHeader
        title="Field Sales · Extras"
        subtitle="Territories, targets, sample consignments, daily report"
        icon={Layers}
      />

      <div className="inline-flex rounded-full bg-white border border-[#E4DFCF] p-1 text-sm" data-testid="field-extras-tabs">
        {[
          { k: "territories", label: "Territories", icon: MapPin },
          { k: "targets",     label: "Targets",     icon: TargetIcon },
          { k: "consignments",label: "Consignments",icon: PackageOpen },
          { k: "daily",       label: "Daily Report",icon: TrendingUp },
        ].map(({ k, label, icon: Icon }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded-full inline-flex items-center gap-1 ${tab === k ? "bg-[#0F5E39] text-white" : "text-[#4A5A50]"}`}
            data-testid={`field-tab-${k}`}
          >
            <Icon size={14}/> {label}
          </button>
        ))}
      </div>

      {tab === "territories"  && <TerritoriesPanel/>}
      {tab === "targets"      && <TargetsPanel/>}
      {tab === "consignments" && <ConsignmentsPanel/>}
      {tab === "daily"        && <DailyReportPanel/>}
    </div>
  );
}


function useSalespeople() {
  const [team, setTeam] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/field-sales/team/salespeople");
        setTeam(Array.isArray(data) ? data : (data.items || []));
      } catch (_) { /* noop */ }
    })();
  }, []);
  return team;
}


// -------------------------------------------------------------- Territories
function TerritoriesPanel() {
  const team = useSalespeople();
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [state, setState] = useState("");
  const [pin, setPin] = useState("");
  const [rep, setRep] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/field-sales/territories");
    setRows(Array.isArray(data) ? data : (data.items || []));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.warning("Name is required");
    setBusy(true);
    try {
      await api.post("/field-sales/territories", {
        name: name.trim(),
        state: state.trim() || null,
        pin_codes: pin.split(",").map((s) => s.trim()).filter(Boolean),
        assigned_sp_ids: rep ? [rep] : [],
      });
      setName(""); setState(""); setPin(""); setRep("");
      toast.success("Territory created");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this territory?")) return;
    await api.delete(`/field-sales/territories/${id}`);
    load();
  };

  return (
    <div className="space-y-4" data-testid="territories-panel">
      <Card><CardContent className="p-4 space-y-3">
        <div className="font-serif text-lg">New territory</div>
        <div className="grid md:grid-cols-4 gap-3">
          <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Mumbai West" data-testid="territory-name-input"/></div>
          <div><Label>State</Label><Input value={state} onChange={(e) => setState(e.target.value)} placeholder="Maharashtra"/></div>
          <div><Label>PIN codes (comma)</Label><Input value={pin} onChange={(e) => setPin(e.target.value)} placeholder="400001,400002"/></div>
          <div>
            <Label>Assign rep</Label>
            <select value={rep} onChange={(e) => setRep(e.target.value)} className="w-full h-10 rounded-md border border-[#E4DFCF] px-3 bg-white" data-testid="territory-rep-select">
              <option value="">— optional —</option>
              {team.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end">
          <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" disabled={busy} onClick={create} data-testid="territory-create-btn"><Plus size={14} className="mr-1"/>Create</Button>
        </div>
      </CardContent></Card>

      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-[#4A5A50] italic">No territories yet.</p>}
        {rows.map((t) => (
          <Card key={t.id}><CardContent className="p-4 flex items-center gap-3" data-testid={`territory-row-${t.id}`}>
            <MapPin size={18} className="text-[#C9A24B]"/>
            <div className="flex-1">
              <div className="font-serif text-lg">{t.name} {t.state && <span className="text-sm text-[#4A5A50]">· {t.state}</span>}</div>
              <div className="text-xs text-[#4A5A50]">
                {t.pin_codes?.length ? `PINs: ${t.pin_codes.slice(0, 6).join(", ")}${t.pin_codes.length > 6 ? "…" : ""}` : "No PINs"}
                {" · "}{t.assigned_sp_ids?.length || 0} rep(s)
              </div>
            </div>
            <Button size="sm" variant="outline" className="rounded-full text-red-700 border-red-200 hover:bg-red-50" onClick={() => del(t.id)} data-testid={`territory-del-${t.id}`}><Trash2 size={13}/></Button>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------- Targets
function TargetsPanel() {
  const team = useSalespeople();
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [rep, setRep] = useState("");
  const [amount, setAmount] = useState("");
  const [orders, setOrders] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/field-sales/targets?period_month=${period}`);
    setRows(data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const create = async () => {
    if (!rep || !amount) return toast.warning("Rep and amount required");
    setBusy(true);
    try {
      await api.post("/field-sales/targets", {
        sales_person_id: rep,
        period_month: period,
        target_amount: Number(amount),
        target_orders: orders ? Number(orders) : null,
      });
      setAmount(""); setOrders("");
      toast.success("Target saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="targets-panel">
      <Card><CardContent className="p-4 space-y-3">
        <div className="grid md:grid-cols-4 gap-3">
          <div><Label>Period (YYYY-MM)</Label><Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="target-period-input"/></div>
          <div>
            <Label>Rep</Label>
            <select value={rep} onChange={(e) => setRep(e.target.value)} className="w-full h-10 rounded-md border border-[#E4DFCF] px-3 bg-white" data-testid="target-rep-select">
              <option value="">— select —</option>
              {team.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select>
          </div>
          <div><Label>Target ₹</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="500000" data-testid="target-amount-input"/></div>
          <div><Label>Order count (opt.)</Label><Input type="number" value={orders} onChange={(e) => setOrders(e.target.value)} placeholder="10"/></div>
        </div>
        <div className="flex justify-end">
          <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" disabled={busy} onClick={create} data-testid="target-save-btn"><Plus size={14} className="mr-1"/>Save target</Button>
        </div>
      </CardContent></Card>

      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-[#4A5A50] italic">No targets set for {period}.</p>}
        {rows.map((t) => {
          const emp = team.find((u) => u.id === t.sales_person_id);
          const pct = t.achievement_pct || 0;
          return (
            <Card key={t.id}><CardContent className="p-4" data-testid={`target-row-${t.id}`}>
              <div className="flex items-center gap-3">
                <TargetIcon size={18} className="text-[#C9A24B]"/>
                <div className="flex-1">
                  <div className="font-serif text-lg">{emp?.name || t.sales_person_id.slice(0, 8)}</div>
                  <div className="text-xs text-[#4A5A50]">₹{t.achieved_amount.toLocaleString("en-IN")} / ₹{t.target_amount.toLocaleString("en-IN")} · {t.achieved_orders} orders</div>
                </div>
                <div className={`font-serif text-2xl ${pct >= 100 ? "text-[#0F5E39]" : pct >= 60 ? "text-[#C9A24B]" : "text-red-600"}`}>{pct}%</div>
              </div>
              <div className="mt-3 h-2 rounded-full bg-[#F1EDDF] overflow-hidden">
                <div className={`h-full ${pct >= 100 ? "bg-[#0F5E39]" : pct >= 60 ? "bg-[#C9A24B]" : "bg-red-500"}`} style={{ width: `${Math.min(100, pct)}%` }}/>
              </div>
            </CardContent></Card>
          );
        })}
      </div>
    </div>
  );
}


// ------------------------------------------------------------- Consignments
function ConsignmentsPanel() {
  const team = useSalespeople();
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [rep, setRep] = useState("");
  const [contact, setContact] = useState("");
  const [items, setItems] = useState([{ design_code: "", qty_issued: 1, weight_issued: "" }]);
  const [busy, setBusy] = useState(false);
  const [returnFor, setReturnFor] = useState(null);
  const load = async () => {
    const { data } = await api.get("/field-sales/consignments");
    setRows(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const addLine = () => setItems([...items, { design_code: "", qty_issued: 1, weight_issued: "" }]);
  const setLine = (i, patch) => setItems(items.map((r, j) => j === i ? { ...r, ...patch } : r));
  const rmLine = (i) => setItems(items.filter((_, j) => j !== i));

  const create = async () => {
    if (!rep) return toast.warning("Select rep");
    const bad = items.some((r) => !r.weight_issued || Number(r.weight_issued) <= 0);
    if (bad) return toast.warning("Every line needs a weight in grams");
    setBusy(true);
    try {
      await api.post("/field-sales/consignments", {
        sales_person_id: rep,
        contact_name: contact.trim() || null,
        items: items.map((r) => ({
          design_code: r.design_code || null,
          qty_issued: Number(r.qty_issued) || 1,
          weight_issued: Number(r.weight_issued),
        })),
      });
      toast.success("Consignment issued");
      setShowForm(false); setRep(""); setContact("");
      setItems([{ design_code: "", qty_issued: 1, weight_issued: "" }]);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="consignments-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#4A5A50]">Track pieces issued to reps against the metal weight ledger.</div>
        <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={() => setShowForm(!showForm)} data-testid="consignment-new-btn"><Plus size={14} className="mr-1"/>Issue new</Button>
      </div>

      {showForm && (
        <Card><CardContent className="p-4 space-y-3" data-testid="consignment-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Rep</Label>
              <select value={rep} onChange={(e) => setRep(e.target.value)} className="w-full h-10 rounded-md border border-[#E4DFCF] px-3 bg-white" data-testid="consignment-rep-select">
                <option value="">— select —</option>
                {team.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
            </div>
            <div><Label>Shown to (contact)</Label><Input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Sharma Jewels"/></div>
          </div>
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Items</div>
            {items.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`consignment-item-${i}`}>
                <div className="col-span-5"><Label>Design code</Label><Input value={r.design_code} onChange={(e) => setLine(i, { design_code: e.target.value })} placeholder="BR-201"/></div>
                <div className="col-span-2"><Label>Qty</Label><Input type="number" value={r.qty_issued} onChange={(e) => setLine(i, { qty_issued: e.target.value })}/></div>
                <div className="col-span-4"><Label>Weight (g)</Label><Input type="number" step="0.01" value={r.weight_issued} onChange={(e) => setLine(i, { weight_issued: e.target.value })} data-testid={`consignment-weight-${i}`}/></div>
                <div className="col-span-1"><Button size="icon" variant="outline" onClick={() => rmLine(i)} className="text-red-600"><Trash2 size={13}/></Button></div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addLine} className="rounded-full" data-testid="consignment-add-line"><Plus size={12} className="mr-1"/>Add line</Button>
          </div>
          <div className="flex justify-end">
            <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" disabled={busy} onClick={create} data-testid="consignment-issue-btn">Issue</Button>
          </div>
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-[#4A5A50] italic">No consignments yet.</p>}
        {rows.map((c) => {
          const emp = team.find((u) => u.id === c.sales_person_id);
          const disc = c.weight_discrepancy || 0;
          return (
            <Card key={c.id}><CardContent className="p-4" data-testid={`consignment-row-${c.id}`}>
              <div className="flex items-center gap-3">
                <PackageOpen size={18} className="text-[#C9A24B]"/>
                <div className="flex-1">
                  <div className="font-serif text-lg">{c.slip_code} · {emp?.name || c.sales_person_id.slice(0, 8)}</div>
                  <div className="text-xs text-[#4A5A50]">
                    Issued {new Date(c.issued_at).toLocaleDateString("en-IN")} · {c.items?.length || 0} pcs · out {c.total_weight_issued}g
                    {c.status !== "issued" && <> · returned {c.total_weight_returned}g</>}
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${c.status === "closed" ? "bg-[#E1F0E6] text-[#0F5E39]" : c.status === "partial_return" ? "bg-[#FFF6E1] text-[#8A6600]" : "bg-[#F1EDDF] text-[#4A5A50]"}`}>{c.status.replace("_", " ")}</span>
                {c.status !== "closed" && (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setReturnFor(c)} data-testid={`consignment-return-btn-${c.id}`}><RotateCcw size={13} className="mr-1"/>Return</Button>
                )}
              </div>
              {disc !== 0 && c.status === "closed" && (
                <div className={`mt-2 text-xs px-2 py-1 rounded-full inline-block ${Math.abs(disc) > 0.5 ? "bg-red-50 text-red-700" : "bg-[#FFF6E1] text-[#8A6600]"}`}>
                  Discrepancy: {disc > 0 ? "+" : ""}{disc.toFixed(2)}g
                </div>
              )}
            </CardContent></Card>
          );
        })}
      </div>

      {returnFor && <ReturnDialog consignment={returnFor} onClose={() => { setReturnFor(null); load(); }}/>}
    </div>
  );
}


function ReturnDialog({ consignment, onClose }) {
  const [items, setItems] = useState(consignment.items.map((it) => ({
    ...it,
    weight_returned: it.weight_issued,
    qty_returned: it.qty_issued,
    status: "returned",
  })));
  const [busy, setBusy] = useState(false);
  const setLine = (i, patch) => setItems(items.map((r, j) => j === i ? { ...r, ...patch } : r));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/field-sales/consignments/${consignment.id}/return`, {
        items: items.map((r) => ({
          design_code: r.design_code, qty_issued: r.qty_issued, weight_issued: r.weight_issued,
          qty_returned: r.qty_returned == null ? null : Number(r.qty_returned),
          weight_returned: r.weight_returned == null ? null : Number(r.weight_returned),
          status: r.status,
        })),
      });
      toast.success("Return recorded");
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Return failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FAF7EF] rounded-3xl w-full max-w-2xl p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="consignment-return-dialog">
        <div className="font-serif text-xl mb-1">Return · {consignment.slip_code}</div>
        <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mb-3">Reconcile each piece</div>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
          {items.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end bg-white p-2 rounded-xl border border-[#E4DFCF]">
              <div className="col-span-3 text-xs">{r.design_code || "—"}<div className="text-[10px] text-[#4A5A50]">out: {r.weight_issued}g</div></div>
              <div className="col-span-3"><Label className="text-[10px]">Wt returned (g)</Label><Input type="number" step="0.01" value={r.weight_returned} onChange={(e) => setLine(i, { weight_returned: e.target.value })} data-testid={`return-wt-${i}`}/></div>
              <div className="col-span-2"><Label className="text-[10px]">Qty ret</Label><Input type="number" value={r.qty_returned || 0} onChange={(e) => setLine(i, { qty_returned: e.target.value })}/></div>
              <div className="col-span-4">
                <Label className="text-[10px]">Status</Label>
                <select value={r.status} onChange={(e) => setLine(i, { status: e.target.value })} className="w-full h-10 rounded-md border border-[#E4DFCF] px-2 bg-white text-sm" data-testid={`return-status-${i}`}>
                  <option value="returned">Returned</option>
                  <option value="sold">Sold</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" className="rounded-full" onClick={onClose}>Cancel</Button>
          <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" disabled={busy} onClick={submit} data-testid="return-submit-btn">Record return</Button>
        </div>
      </div>
    </div>
  );
}


// -------------------------------------------------------------- Daily Report
function DailyReportPanel() {
  const team = useSalespeople();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [rep, setRep] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams({ date });
        if (rep) params.set("sales_person_id", rep);
        const { data: d } = await api.get(`/field-sales/reports/daily?${params}`);
        setData(d);
      } catch (_) { setData(null); }
    })();
  }, [date, rep]);

  return (
    <div className="space-y-4" data-testid="daily-report-panel">
      <Card><CardContent className="p-4 grid md:grid-cols-3 gap-3">
        <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="daily-date-input"/></div>
        <div>
          <Label>Rep (opt.)</Label>
          <select value={rep} onChange={(e) => setRep(e.target.value)} className="w-full h-10 rounded-md border border-[#E4DFCF] px-3 bg-white">
            <option value="">— all —</option>
            {team.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
        </div>
      </CardContent></Card>

      {data && (
        <div className="grid md:grid-cols-3 gap-3">
          <StatCard label="Visits"        value={data.visits}         color="#0F5E39"/>
          <StatCard label="Orders"        value={`${data.orders_count} · ₹${(data.orders_amount||0).toLocaleString("en-IN")}`} color="#C9A24B"/>
          <StatCard label="Follow-ups done" value={data.follow_ups_done} color="#0F5E39"/>
          <StatCard label="Expenses (₹)"  value={`${(data.expenses_amount||0).toLocaleString("en-IN")} (${data.expenses_count} claims)`} color="#8A6600"/>
        </div>
      )}
    </div>
  );
}


function StatCard({ label, value, color }) {
  return (
    <Card><CardContent className="p-4">
      <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{label}</div>
      <div className="font-serif text-2xl mt-1" style={{ color }}>{value}</div>
    </CardContent></Card>
  );
}
